import type {
  BankIncomingAdminLine,
  BankIncomingAuditEntry,
  BankIncomingSummary,
  BankTransactionType,
  IncomingMatchStatus,
  OpenItem,
  OpenItemKind,
} from '@/types/bank-reconciliation';
import type {
  FundCostDesignate,
  FundCostDesignateResponse,
} from '@/types/fund-costs';
import type { DealCostFromBankLineCreate } from '@/types/deal-costs';
import { apiClient } from '@/utils/api-client';
import { toNumber, toNumberOrNull } from '@/utils/number-operations';

export interface BankIncomingListResponse {
  items: BankIncomingAdminLine[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface BankIncomingFilters {
  fund_id?: string;
  fund_bank_account_id?: string;
  match_status?: IncomingMatchStatus;
  transaction_type?: BankTransactionType;
  suggested_only?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

/**
 * Which door the module is entered through. The admin module (no scope) spans
 * every fund; a fund scope narrows it to one fund's own endpoints, which a user
 * with fund permissions can reach without being an admin.
 */
export interface BankIncomingScope {
  fundId: string;
}

const basePath = (scope?: BankIncomingScope): string =>
  scope
    ? `/funds/${scope.fundId}/incoming-transactions`
    : '/admin/bank-incoming';

const toQuery = (filters: BankIncomingFilters | OpenItemFilters): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * The API serialises Decimal as a string; the line type promises a number.
 */
const mapLine = (line: BankIncomingAdminLine): BankIncomingAdminLine => ({
  ...line,
  amount: toNumber(line.amount),
  allocated_amount: toNumber(line.allocated_amount ?? 0),
  unapplied_amount: toNumber(line.unapplied_amount ?? 0),
  suggested_allocations: (line.suggested_allocations ?? []).map(
    (allocation) => ({ ...allocation, amount: toNumber(allocation.amount) })
  ),
  allocations: (line.allocations ?? []).map((allocation) => ({
    ...allocation,
    amount: toNumber(allocation.amount),
    cleared_return_amount: toNumberOrNull(allocation.cleared_return_amount),
  })),
});

export const bankIncomingService = {
  async list(
    filters: BankIncomingFilters = {},
    scope?: BankIncomingScope
  ): Promise<BankIncomingListResponse> {
    const page = await apiClient.get<BankIncomingListResponse>(
      `${basePath(scope)}${toQuery(filters)}`
    );
    return { ...page, items: page.items.map(mapLine) };
  },

  async summary(
    filters: BankIncomingFilters = {},
    scope?: BankIncomingScope
  ): Promise<BankIncomingSummary> {
    return await apiClient.get(`${basePath(scope)}/summary${toQuery(filters)}`);
  },

  async get(
    incomingId: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.get<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}`
      )
    );
  },

  async audit(
    incomingId: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAuditEntry[]> {
    return await apiClient.get(`${basePath(scope)}/${incomingId}/audit`);
  },

  async accept(
    incomingId: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.post<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}/accept`,
        {}
      )
    );
  },

  async reject(
    incomingId: string,
    reason?: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.post<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}/reject`,
        { reason: reason || null }
      )
    );
  },

  async bulkAccept(
    incomingIds: string[],
    scope?: BankIncomingScope
  ): Promise<BulkAcceptResult> {
    return await apiClient.post(`${basePath(scope)}/bulk-accept`, {
      incoming_ids: incomingIds,
    });
  },

  /** The open items this line could settle, for the Match Manually screen. */
  async openItems(
    incomingId: string,
    filters: OpenItemFilters = {},
    scope?: BankIncomingScope
  ): Promise<OpenItemListResponse> {
    return await apiClient.get(
      `${basePath(scope)}/${incomingId}/open-items${toQuery(filters)}`
    );
  },

  /** Match the line to the single open item the operator picked (exact 1:1). */
  async matchOpenItem(
    incomingId: string,
    target: OpenItemMatchPayload,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return await apiClient.post(`${basePath(scope)}/${incomingId}/match`, {
      fund_transaction_id: target.fund_transaction_id ?? null,
      captable_transaction_id: target.captable_transaction_id ?? null,
      drawdown_investor_id: target.drawdown_investor_id ?? null,
    });
  },

  /** Books the whole outgoing line as a deal cost on one of the fund's investments. */
  async bookAsDealCost(
    fundId: string,
    incomingId: string,
    data: DealCostFromBankLineCreate
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.post<BankIncomingAdminLine>(
        `${basePath({ fundId })}/${incomingId}/deal-cost`,
        data
      )
    );
  },

  /** Settles a move between the fund's own accounts. Books nothing. */
  async markInternalTransfer(
    incomingId: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.post<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}/internal-transfer`,
        {}
      )
    );
  },

  async split(
    incomingId: string,
    allocations: SplitAllocationPayload[],
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.post<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}/split`,
        { allocations }
      )
    );
  },

  /**
   * Reverses one placement. The rest of the line's allocations stand, so the
   * line comes back for this much and not for all of it.
   */
  async unlinkAllocation(
    incomingId: string,
    allocationId: string,
    scope?: BankIncomingScope
  ): Promise<BankIncomingAdminLine> {
    return mapLine(
      await apiClient.delete<BankIncomingAdminLine>(
        `${basePath(scope)}/${incomingId}/allocations/${allocationId}`
      )
    );
  },

  /**
   * Uploads a bank statement export. Fund-scoped only: a statement is drawn on
   * one fund's account, so there is no admin-wide equivalent.
   */
  async importStatement(
    file: File,
    options: StatementImportOptions,
    scope: BankIncomingScope
  ): Promise<StatementImportResult> {
    const body = new FormData();
    body.append('file', file);
    body.append('dry_run', String(options.dryRun ?? false));
    body.append(
      'auto_settle_internal_transfers',
      String(options.autoSettleInternalTransfers ?? false)
    );
    return await apiClient.post(`${basePath(scope)}/import`, body);
  },

  /** Fund-scoped only. */
  async refreshSuggestions(scope: BankIncomingScope): Promise<void> {
    await apiClient.post(`${basePath(scope)}/refresh-suggestions`);
  },

  /** Fund-scoped only. */
  async designateFundCost(
    incomingId: string,
    data: FundCostDesignate,
    scope: BankIncomingScope
  ): Promise<FundCostDesignateResponse> {
    return await apiClient.post(
      `${basePath(scope)}/${incomingId}/designate-fund-cost`,
      data
    );
  },
};

export interface BulkAcceptResult {
  accepted: string[];
  failed: Record<string, string>;
}

export interface OpenItemFilters {
  search?: string;
  kind?: OpenItemKind;
  page?: number;
  page_size?: number;
}

export interface OpenItemListResponse {
  items: OpenItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface OpenItemMatchPayload {
  // Exactly one target, as the endpoint requires: a fund transaction, or the
  // portfolio transaction whose bank entry this line becomes.
  fund_transaction_id?: string | null;
  captable_transaction_id?: string | null;
  drawdown_investor_id?: string | null;
}

export interface SplitAllocationPayload {
  amount: number;
  drawdown_investor_id?: string;
  fund_transaction_id?: string;
  /** Settles nothing: held under this investor's name until it is attributed. */
  unapplied_investor_id?: string;
  /** Settles nothing: moves the fund's current account with this manager. */
  fund_manager_entity_id?: string;
  /** Settles nothing: pairs this line with an opposite one. */
  counter_line_id?: string;
  /** A payment the fund made coming back: negative coverage against the item. */
  returned_fund_transaction_id?: string;
}

export interface RowRejection {
  line_number: number;
  reason: string;
}

export interface StatementImportResult {
  rows_read: number;
  inserted: number;
  duplicates: number;
  already_received: number;
  rejections: RowRejection[];
  lines_matched: number;
  lines_suggested: number;
  lines_settled_by_rule: number;
  dry_run: boolean;
}

export interface StatementImportOptions {
  dryRun?: boolean;
  autoSettleInternalTransfers?: boolean;
}
